import { useEffect, useRef } from 'react'
import { useToggle } from 'react-use'
import { useParams } from 'react-router-dom'
import { BACKEND_URL } from '@const/env'
import {
  usePostEndMutation,
  usePostIspringTestResultsMutation,
  usePostPauseMutation,
  usePostStartMutation,
  usePostTestResultsMutation,
} from '@services/statistic'
import useMe from '@hooks/useMe'
import { ScormEvents } from '@enums/ScormEvents'
import { CourseTypes } from '@models/course'
import { log } from '@utils/console'
import {
  createStatsCommonForm,
  toEducationDestinationIri,
} from '@utils/courseThemeStats'

type ScormEvent = {
  data: { type: ScormEvents; data: Record<string, unknown> }
  origin: string
}

type IspringScormEvent = {
  data: {
    type: ScormEvents
    awardedPercent: string
    awardedScore: string
    eventTime: Date
    passingPercent: string
    passingScore: string
    quizDuration: string
    quizStatus: string
  }
  origin: string
}

const normalizeQuizStatus = (status?: string): boolean => {
  if (!status) {
    return false
  }

  return [
    'passed',
    'pass',
    'успешно',
    'true',
    '1',
    'completed',
    'complete',
  ].includes(status.toLowerCase())
}

const normalizeScormNumber = (value?: string): number => {
  if (!value) {
    return 0
  }

  const normalizedValue = value.replace(',', '.')
  const matchedNumber = normalizedValue.match(/-?\d+(?:\.\d+)?/)

  return matchedNumber ? Number(matchedNumber[0]) : 0
}

const normalizeScormDuration = (value?: string): number => {
  if (!value) {
    return 0
  }

  const parts = value.split(':').map((part) => Number(part))

  if (parts.length === 3 && parts.every((part) => Number.isFinite(part))) {
    const [hours, minutes, seconds] = parts

    return hours * 3600 + minutes * 60 + seconds
  }

  return normalizeScormNumber(value)
}

const isSpringScormPassed = (
  quizStatus: string | undefined,
  awardedPercent?: string,
  passingPercent?: string,
): boolean => {
  const normalizedPassingPercent = normalizeScormNumber(passingPercent)
  const normalizedAwardedPercent = normalizeScormNumber(awardedPercent)

  if (normalizedPassingPercent > 0) {
    return normalizedAwardedPercent >= normalizedPassingPercent
  }

  return normalizeQuizStatus(quizStatus)
}

/**
 * Determine if stats should be skipped for this course.
 * For free courses, stats are always recorded (backend creates virtual assignment).
 * For mini courses, stats are recorded without education destination.
 */
const shouldSkipStats = (
  educationId: string,
  type: CourseTypes | undefined,
  isFree: boolean | undefined
): boolean => {
  log('shouldSkipStats', { type, educationId, isFree })

  // Never skip for free courses - backend will create virtual assignment
  if (isFree === true) {
    return false
  }

  // Mini courses record stats without education
  if (type === CourseTypes.MINI) {
    return false
  }

  // For regular courses, skip if no educationId
  return educationId === '0' || !educationId
}

// Окно после end, в котором повторный ISPRING считаем спурьёзным дублем iSpring, а не
// пересдачей. Дубль прилетает через миллисекунды после завершения (его слать нельзя —
// бэкенд ответит «Вы еще не начали проходить тему»), пересдача «Повторить тест» —
// только через секунды (человек заново отвечает на вопросы).
const RETRY_GRACE_MS = 3000

const useScormEventSender = (
  type: CourseTypes | undefined,
  scormType: 'articulate' | 'ispring' | undefined,
  test: boolean | undefined,
  isFree?: boolean,
  hasControlQuestion: boolean | undefined = undefined,
  isControlQuestionPassed = false,
  skipStats = false,
  // Отдаём наружу реальное назначение: у бесплатных курсов (education/0) во фронте
  // его нет, но бэкенд создаёт его сам при старте SCORM, и хук его знает.
  onFinalTestPassed?: (educationDestinationIri?: string) => void
) => {
  const { themeId = '', educationId = '' } = useParams()
  const lastMessageRef = useRef('')
  // Track the actual educationDestination IRI (may be auto-created by backend)
  const educationDestinationRef = useRef<string | undefined>(undefined)
  // Ensures the "final test passed" callback fires at most once per mount.
  const finalTestPassedFiredRef = useRef(false)
  // iSpring может прислать результаты повторно. Первый POST проходит, дальше
  // handleEnd закрывает тему, и повторный POST прилетает уже в завершённую тему —
  // бэкенд принимает результаты только у незавершённой и отвечает «Вы еще не начали
  // проходить тему». Поэтому одинаковые результаты шлём один раз (как в handleResults).
  const lastIspringResultsRef = useRef('')
  // Тема завершена (прошёл end). После этого поздние результаты/iSpring-события НЕ шлём:
  // их бэкенд принимает только у незавершённой темы и иначе отвечает «Вы еще не начали
  // проходить тему» (в углу всплывал тост). Дедуп по содержимому не спасал: iSpring мог
  // прислать второе событие с ДРУГИМ payload. Ставится синхронно в handleEnd.
  const themeEndedRef = useRef(false)
  // Момент последнего end (Date.now). По нему в handleSpringResults отличаем спурьёзный
  // дубль (сразу после end) от пересдачи (через секунды). См. RETRY_GRACE_MS.
  const endedAtRef = useRef(0)

  const { isAdmin } = useMe()

  const [started, toggleStarted] = useToggle(false)
  const [ended, toggleEnded] = useToggle(false)

  const [postStart] = usePostStartMutation()
  const [postEnd] = usePostEndMutation()
  const [postPause] = usePostPauseMutation()
  const [postResults] = usePostTestResultsMutation()
  const [postIspringResults] = usePostIspringTestResultsMutation()

  // Convert URL educationId to IRI
  const initialEducationDestinationIri = toEducationDestinationIri(educationId)

  // Get current educationDestination (prefer auto-created over initial)
  const getEducationDestination = (): string | undefined => {
    return educationDestinationRef.current || initialEducationDestinationIri
  }

  const handleInit = async () => {
    log('handleInit', started)
    if (started) return

    const commonForm = createStatsCommonForm(themeId, initialEducationDestinationIri)

    if (lastMessageRef.current !== JSON.stringify(commonForm) + 'INIT') {
      try {
        // Start the theme - backend may return auto-created educationDestination
        const startResponse = await postStart(commonForm).unwrap()

        // Store the actual educationDestination IRI (may be auto-created)
        if (startResponse.educationDestination) {
          educationDestinationRef.current = startResponse.educationDestination
          log('handleInit: received educationDestination', startResponse.educationDestination)
        }

        lastMessageRef.current = JSON.stringify(commonForm) + 'INIT'
        toggleStarted(true)

        log('handleInit success', JSON.stringify(commonForm))
      } catch (error) {
        log('handleInit error', error)
      }
    }
  }

  const handleEnd = async (testSuccess: boolean): Promise<void> => {
    if (hasControlQuestion === true || hasControlQuestion === undefined) {
      log('handleEnd skipped: control question present', {
        themeId,
      })

      return
    }

    log('handleEnd', ended)
    if (ended) return

    const commonForm = createStatsCommonForm(themeId, getEducationDestination())

    const newMessage = {
      ...commonForm,
      testSuccess,
    }
    if (lastMessageRef.current !== JSON.stringify(newMessage) + 'END') {
      // Метки ставим ДО await: чтобы повторный вызов не отправил end второй раз и чтобы
      // поздние результаты в уже завершённую тему не улетали (см. themeEndedRef).
      lastMessageRef.current = JSON.stringify(newMessage) + 'END'
      themeEndedRef.current = true
      endedAtRef.current = Date.now()
      toggleEnded(true)

      // Ждём завершения end. У SCORM-итогового навигация на результаты идёт сразу после
      // этого, и без ожидания страница результатов успевала перезапросить назначение ДО
      // того, как бэкенд зачёл итоговый (пересчёт статуса/счётчиков — на postEnd), из-за
      // чего мигала плашка «Осталось пройти незавершённые темы».
      try {
        await postEnd(newMessage).unwrap()
      } catch (error) {
        log('handleEnd error', error)
      }

      log('handleEnd success', JSON.stringify(commonForm))
    }
  }

  const handleResults = (event: ScormEvent) => {
    log('handleResults')
    // Тема уже завершена: результаты бэкенд отклонит («Вы еще не начали проходить тему»).
    if (themeEndedRef.current) {
      return
    }
    const commonForm = createStatsCommonForm(themeId, getEducationDestination())

    const newMessage = {
      ...commonForm,
      questionNumber: event?.data?.data?.questionNumber as number,
      questionCount: event?.data?.data?.questionsCount as number,
      success: event?.data?.data?.success as boolean,
    }
    if (lastMessageRef.current !== JSON.stringify(newMessage) + 'TEST') {
      postResults(newMessage)
      lastMessageRef.current = JSON.stringify(newMessage) + 'TEST'

      log('handleResults success', JSON.stringify(commonForm))
    }
  }

  const handleSpringResults = async (event: IspringScormEvent) => {
    log('handleSpringResults', test)
    // Тема уже завершена. Отличаем спурьёзный дубль iSpring (прилетает через миллисекунды
    // после end — слать нельзя, бэкенд ответит «Вы еще не начали проходить тему») от
    // пересдачи «Повторить тест» (приходит только через секунды, человек заново отвечает).
    if (themeEndedRef.current) {
      if (Date.now() - endedAtRef.current < RETRY_GRACE_MS) {
        return
      }
      // За окном — это пересдача «Повторить тест». Заводим НОВУЮ попытку: шлём start, чтобы
      // каждая пересдача писалась отдельной строкой в ведомости (полная история попыток).
      // iSpring на «Повторить тест» не присылает INIT, поэтому start инициируем сами и
      // ДОЖИДАЕМСЯ его — тогда последующие ispring/end лягут в свежесозданную попытку, а не в
      // прошлую (бэкенд-start создаёт новую строку, если прошлая уже завершена). Спурьёзный
      // дубль в окне выше остаётся заблокирован.
      log('handleSpringResults: retry detected, starting new attempt')
      try {
        const retryStartForm = createStatsCommonForm(themeId, getEducationDestination())
        const retryStartResponse = await postStart(retryStartForm).unwrap()
        if (retryStartResponse.educationDestination) {
          educationDestinationRef.current = retryStartResponse.educationDestination
        }
      } catch (error) {
        // start не прошёл — не шлём ispring/end, иначе они попадут в прошлую попытку и затрут
        // её. themeEndedRef оставляем true: следующее событие снова попробует пересдачу.
        log('handleSpringResults: retry start failed, aborting retry', error)
        return
      }
      themeEndedRef.current = false
      lastIspringResultsRef.current = ''
      lastMessageRef.current = ''
      toggleEnded(false)
    }
    if (test) {
      const commonForm = createStatsCommonForm(themeId, getEducationDestination())
      const isPassed = isSpringScormPassed(
        event.data.quizStatus,
        event.data.awardedPercent,
        event.data.passingPercent
      )

      const ispringResults = {
        ...commonForm,
        passingScore: normalizeScormNumber(event.data.passingScore),
        passingPercent: normalizeScormNumber(event.data.passingPercent),
        awardedScore: normalizeScormNumber(event.data.awardedScore),
        awardedPercent: normalizeScormNumber(event.data.awardedPercent),
        quizDuration: normalizeScormDuration(event.data.quizDuration),
      }
      const ispringResultsKey = JSON.stringify(ispringResults) + 'ISPRING'

      if (lastIspringResultsRef.current !== ispringResultsKey) {
        lastIspringResultsRef.current = ispringResultsKey
        await postIspringResults(ispringResults)
      }

      // Дожидаемся записи end на бэкенде и только потом уводим на результаты, иначе там
      // счётчики тем ещё без учёта итогового и мигает плашка «Осталось пройти».
      await handleEnd(isPassed)

      // Итоговый SCORM-тест пройден: показываем сообщение о прохождении курса
      // (для ручного/xls это делает handleFinalTestContinue; у SCORM своего
      // "продолжить" нет, поэтому дергаем колбэк здесь).
      if (isPassed && !finalTestPassedFiredRef.current) {
        finalTestPassedFiredRef.current = true
        onFinalTestPassed?.(getEducationDestination())
      }
      return
    }

    void handleEnd(normalizeQuizStatus(event.data.quizStatus))
  }

  useEffect(() => {
    toggleStarted(false)
    toggleEnded(false)
    educationDestinationRef.current = undefined
    finalTestPassedFiredRef.current = false
    lastIspringResultsRef.current = ''
    themeEndedRef.current = false
    endedAtRef.current = 0

    if (skipStats) {
      return
    }

    log('COURSE CHANGED EFFECT', themeId, educationId, type, scormType, isFree)
    if (shouldSkipStats(educationId, type, isFree)) {
      return
    }

    if (scormType === 'ispring') {
      void handleInit()
    }
  }, [themeId, educationId, type, scormType, isFree, skipStats])

  const listener = (event: ScormEvent) => {
    if (!skipStats && !isAdmin && event.origin === BACKEND_URL && themeId) {
      log(
        'listener params',
        JSON.stringify({ themeId, educationId, type, scormType, isFree })
      )
      log('SCORM EVENT:', event.data)
      switch (event.data.type) {
        case ScormEvents.INIT:
          void handleInit()
          break
        case ScormEvents.END:
          void handleEnd(!!event?.data?.data?.testSuccess)
          break
        case ScormEvents.TEST_RESULTS:
          handleResults(event)
          break
        case ScormEvents.ISPRING:
          void handleSpringResults(event as unknown as IspringScormEvent)
          break
      }
    }
  }

  useEffect(() => {
    window.removeEventListener('message', listener)
    window.addEventListener('message', listener)

    return () => window.removeEventListener('message', listener)
  }, [themeId, educationId, type, scormType, isFree, test, hasControlQuestion, skipStats])

  return async () => {
    if (skipStats) {
      toggleEnded(false)
      toggleStarted(false)
      lastMessageRef.current = ''
      educationDestinationRef.current = undefined

      return
    }

    if (isControlQuestionPassed) {
      toggleEnded(false)
      toggleStarted(false)
      lastMessageRef.current = ''
      educationDestinationRef.current = undefined

      return
    }

    const commonForm = createStatsCommonForm(themeId, getEducationDestination())

    log(
      'useScormEventSender callback',
      { started, ended },
      JSON.stringify(commonForm)
    )

    if (shouldSkipStats(educationId, type, isFree)) {
      return
    }

    if (started && !ended) {
      await postPause(commonForm)
    }

    toggleEnded(false)
    toggleStarted(false)
    lastMessageRef.current = ''
    educationDestinationRef.current = undefined
  }
}

export default useScormEventSender
